import React, { useState, useEffect } from 'react';
import { ref, update } from 'firebase/database';
import { auth, db } from './firebase';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword, sendEmailVerification } from 'firebase/auth';
import s from './styles';

const REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central',
  'Northern', 'Upper East', 'Upper West', 'Volta', 'Brong-Ahafo',
  'Oti', 'Savannah', 'North East', 'Bono East', 'Ahafo', 'Western North',
];

export default function ProfileTab({ pharmacyId, pharmacyData }) {
  const [form, setForm] = useState({
    pharmacyName: '', ownerName: '', phone: '',
    location: '', region: '', description: '',
    whatsapp: '', email: '',
  });
  const [saving, setSaving]           = useState(false);
  const [locating, setLocating]       = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError]     = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (pharmacyData) {
      setForm({
        pharmacyName: pharmacyData.pharmacyName || '',
        ownerName:    pharmacyData.ownerName || '',
        phone:        pharmacyData.phone || '',
        location:     pharmacyData.location || '',
        region:       pharmacyData.region || '',
        description:  pharmacyData.description || '',
        whatsapp:     pharmacyData.whatsapp || '',
        email:        pharmacyData.email || '',
      });
    }
  }, [pharmacyData]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const detectLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCFPvLe4a2Oog05StOyrz79Z3bfYxNZb9k`);
          const data = await res.json();
          if (data.results?.[0]) upd('location', data.results[0].formatted_address);
        } catch (e) { console.log(e); }
        setLocating(false);
      },
      () => { alert('Could not detect location'); setLocating(false); }
    );
  };

  const handleSaveClick = () => {
    if (!form.pharmacyName || !form.phone) { alert('Pharmacy name and phone are required'); return; }
    setShowAuthModal(true);
    setAuthPassword(''); setAuthError('');
  };

  // Step 1: Verify password → send email link
  const handleAuthAndSave = async () => {
    if (!authPassword) { setAuthError('Please enter your password'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, authPassword);
      await reauthenticateWithCredential(user, credential);
      // Send verification email
      await sendEmailVerification(user);
      setShowAuthModal(false);
      setAuthPassword('');
      setShowEmailModal(true);
    } catch (e) {
      setAuthError('Incorrect password. Please try again.');
    } finally { setAuthLoading(false); }
  };

  // Step 2: Confirm email verified → save
  const handleEmailVerifiedSave = async () => {
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setShowEmailModal(false);
        await doSave();
      } else {
        alert('⚠️ Email not yet verified. Please click the verification link in your email first, then tap this button again.');
      }
    } catch { alert('Error. Please try again.'); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        pharmacyName: form.pharmacyName, ownerName: form.ownerName,
        phone: form.phone, location: form.location, region: form.region,
        description: form.description, whatsapp: form.whatsapp,
        updatedAt: new Date().toISOString(),
      };
      await update(ref(db, `pharmacies/${pharmacyId}`), updateData);
      await update(ref(db, `pharmacies_map/${pharmacyId}`), {
        name: form.pharmacyName, address: form.location,
        region: form.region, phone: form.phone,
      });
      alert('✅ Profile updated successfully! Changes are live on the customer map.');
    } catch { alert('Error saving. Please try again.'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    setPasswordError(''); setPasswordSuccess('');
    if (!authPassword) { setPasswordError('Enter your current password'); return; }
    if (!newPassword || newPassword.length < 6) { setPasswordError('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmNewPassword) { setPasswordError('New passwords do not match'); return; }
    setAuthLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, authPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordSuccess('✅ Password changed successfully!');
      setAuthPassword(''); setNewPassword(''); setConfirmNewPassword('');
      setTimeout(() => { setShowChangePassword(false); setPasswordSuccess(''); }, 2000);
    } catch (e) {
      setPasswordError(e.code === 'auth/wrong-password' ? 'Incorrect current password.' : 'Failed to change password.');
    } finally { setAuthLoading(false); }
  };

  return (
    <div>

      {/* STEP 1 — PASSWORD MODAL */}
      {showAuthModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 36, margin: '0 0 10px' }}>🔐</p>
              <h3 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 6px' }}>Step 1 — Confirm Password</h3>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Enter your login password. A verification email will then be sent to {auth.currentUser?.email}.</p>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Your Password</label>
              <input style={s.input} type="password" placeholder="Enter your current password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }} onKeyDown={e => e.key === 'Enter' && handleAuthAndSave()} autoFocus />
            </div>
            {authError && <div style={{ backgroundColor: '#FEE2E2', borderRadius: 8, padding: 10, marginBottom: 12 }}><p style={{ color: '#B91C1C', fontSize: 13, margin: 0 }}>⚠️ {authError}</p></div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '11px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', fontSize: 14, backgroundColor: '#F7F8FA', color: '#555' }} onClick={() => { setShowAuthModal(false); setAuthPassword(''); setAuthError(''); }}>Cancel</button>
              <button style={{ flex: 2, padding: '11px', backgroundColor: authLoading ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleAuthAndSave} disabled={authLoading}>
                {authLoading ? 'Sending email...' : 'Confirm & Send Email →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 — EMAIL VERIFICATION MODAL */}
      {showEmailModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 40, margin: '0 0 10px' }}>📧</p>
              <h3 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 6px' }}>Step 2 — Check Your Email</h3>
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.7, margin: 0 }}>
                A verification link has been sent to:<br />
                <strong>{auth.currentUser?.email}</strong><br /><br />
                Click the link in your email, then come back and tap <strong>I've verified</strong>.
              </p>
            </div>
            <div style={{ backgroundColor: '#FEF3C7', borderRadius: 8, padding: 12, marginBottom: 20, border: '1px solid #FCD34D' }}>
              <p style={{ fontSize: 12, color: '#92400E', margin: 0 }}>⚠️ Check your spam folder if you don't see the email. The link expires after 1 hour.</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '11px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }} onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button style={{ flex: 2, padding: '11px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleEmailVerifiedSave} disabled={saving}>
                {saving ? 'Saving...' : "✅ I've verified — Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {showChangePassword && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>🔑 Change Password</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={() => { setShowChangePassword(false); setAuthPassword(''); setNewPassword(''); setConfirmNewPassword(''); setPasswordError(''); }}>✕</button>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Current Password</label>
              <input style={s.input} type="password" placeholder="Your current password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>New Password</label>
              <input style={s.input} type="password" placeholder="At least 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Confirm New Password</label>
              <input style={s.input} type="password" placeholder="Type new password again" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} />
            </div>
            {passwordError && <div style={{ backgroundColor: '#FEE2E2', borderRadius: 8, padding: 10, marginBottom: 12 }}><p style={{ color: '#B91C1C', fontSize: 13, margin: 0 }}>⚠️ {passwordError}</p></div>}
            {passwordSuccess && <div style={{ backgroundColor: '#DCFCE7', borderRadius: 8, padding: 10, marginBottom: 12 }}><p style={{ color: '#15803D', fontSize: 13, margin: 0, fontWeight: 600 }}>{passwordSuccess}</p></div>}
            <button style={{ width: '100%', padding: '13px', backgroundColor: authLoading ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }} onClick={handleChangePassword} disabled={authLoading}>
              {authLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🏪 Pharmacy Profile</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Changes require password + email verification to confirm.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ padding: '9px 16px', backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={() => setShowChangePassword(true)}>
            🔑 Change Password
          </button>
          <button style={{ padding: '10px 24px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }} onClick={handleSaveClick} disabled={saving}>
            {saving ? 'Saving...' : '✓ Save Profile'}
          </button>
        </div>
      </div>

      <div style={{ backgroundColor: '#FEF3C7', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #FCD34D', display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 22 }}>🔐</span>
        <p style={{ margin: 0, fontSize: 13, color: '#92400E' }}>
          <strong>Double verification required:</strong> Every profile save requires your password AND an email verification link. This protects your pharmacy details from unauthorised changes.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>💊 Basic Information</h3>
            {[
              { label: 'Pharmacy Name *', key: 'pharmacyName', ph: 'e.g. HealthPlus Pharmacy' },
              { label: 'Owner / Manager Name', key: 'ownerName', ph: 'Full name' },
              { label: 'Phone Number *', key: 'phone', ph: '+233 24 000 0000' },
              { label: 'WhatsApp Number', key: 'whatsapp', ph: '+233 24 000 0000' },
              { label: 'Email Address', key: 'email', ph: 'pharmacy@email.com', type: 'email' },
            ].map(field => (
              <div key={field.key} style={s.fieldGroup}>
                <label style={s.label}>{field.label}</label>
                <input style={s.input} type={field.type || 'text'} placeholder={field.ph} value={form[field.key]} onChange={e => upd(field.key, e.target.value)} />
              </div>
            ))}
          </div>
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB' }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>📝 Description</h3>
            <textarea style={{ ...s.input, height: 100, resize: 'vertical', fontFamily: 'inherit' }} placeholder="About your pharmacy..." value={form.description} onChange={e => upd('description', e.target.value)} />
          </div>
        </div>

        <div>
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>📍 Location</h3>
            <button style={{ width: '100%', padding: '12px', marginBottom: 14, backgroundColor: locating ? '#A8D5C2' : '#E8F8F2', color: '#1D6B52', border: '1.5px solid #1D9E75', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: locating ? 'not-allowed' : 'pointer' }} onClick={detectLocation} disabled={locating}>
              {locating ? '⏳ Detecting...' : '📍 Detect my current location'}
            </button>
            <div style={s.fieldGroup}>
              <label style={s.label}>Street Address</label>
              <input style={s.input} placeholder="e.g. 12 Ring Road, Adum, Kumasi" value={form.location} onChange={e => upd('location', e.target.value)} />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Region</label>
              <select style={s.select} value={form.region} onChange={e => upd('region', e.target.value)}>
                <option value="">Select region</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>🪪 License</h3>
            <div style={s.fieldGroup}>
              <label style={s.label}>FDA License Number</label>
              <input style={{ ...s.input, backgroundColor: '#F7F8FA', color: '#888' }} value={pharmacyData?.licenseNumber || ''} readOnly />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>PCG (Pharmacy Council of Ghana) License Number</label>
              <input style={{ ...s.input, backgroundColor: '#F7F8FA', color: '#888' }} value={pharmacyData?.pcgLicenseNumber || 'Not on file yet'} readOnly />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>PCG License Expiry</label>
              <input
                style={{
                  ...s.input, backgroundColor: '#F7F8FA', color: '#888',
                  ...(pharmacyData?.pcgLicenseExpiry && pharmacyData.pcgLicenseExpiry < new Date().toISOString().slice(0, 10)
                    ? { color: '#B91C1C', fontWeight: 700 } : {}),
                }}
                value={pharmacyData?.pcgLicenseExpiry || 'Not on file yet'}
                readOnly
              />
            </div>
            <p style={{ fontSize: 12, color: '#888', margin: 0 }}>None of these can be changed here — contact pharmdev.org@gmail.com to add or correct your PCG license details. An expired PCG license auto-suspends your account.</p>
          </div>
          <div style={{ backgroundColor: pharmacyData?.status === 'approved' ? '#E8F8F2' : '#FEF3C7', borderRadius: 12, padding: 16, border: `1px solid ${pharmacyData?.status === 'approved' ? '#B2DFD0' : '#FCD34D'}` }}>
            <p style={{ fontWeight: 700, margin: '0 0 4px', color: pharmacyData?.status === 'approved' ? '#1D6B52' : '#92400E' }}>
              {pharmacyData?.status === 'approved' ? '✅ Verified Pharmacy' : '⏳ Pending Verification'}
            </p>
            <p style={{ fontSize: 13, margin: 0, color: pharmacyData?.status === 'approved' ? '#1D9E75' : '#92400E' }}>
              {pharmacyData?.status === 'approved' ? 'Your pharmacy is verified and visible to customers.' : 'Under review — you will be notified when approved.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}